import { CloseButton, SettingsButton, SaveButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import { IDateRange } from '../../../../data/providers/settings/dates/dateRangeProvider';
import {
  ITrendReportSettings,
  ITrendReportSettingsState,
  trendReportSettingsProvider,
} from '../../../../data/providers/settings/reports/trendReportSettingsProvider';
import { ISettingsTemplate } from '../../../../data/providers/settings/reports/types';
import { isValidDateRange } from '../../../../data/providers/utils/isValidDateRange';
import {
  deleteSelectedTemplate,
  saveTrendSettings,
} from '../../../../data/writers/settings/reports/trendReportSettings';
import { Row } from '../../../layout/Row';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { Toggle } from '../../../shared/form/Toggle';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { LeftColumn } from '../../utils/settings/LeftColumn';
import { RightColumn } from '../../utils/settings/RightColumn';
import { TemplateSelect } from '../../utils/settings/TemplateSelect';
import { TimeframeSelector } from '../../utils/settings/TimeframeSelector';
import { TemplateForm } from './TemplateForm';

interface IPopupProps {
  onClose: () => void;
  onSave: () => void;
}

export const Popup = (props: IPopupProps): React.ReactElement => {
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<ISettingsTemplate<ITrendReportSettings>>();
  const [templates, setTemplates] = React.useState<
    Array<ISettingsTemplate<ITrendReportSettings>>
  >([]);
  const [initialSettings, setInitialSettings] =
    React.useState<ISettingsTemplate<ITrendReportSettings>>();
  const [timeframe, setTimeframe] = React.useState<IDateRange>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [whiskersToggled, setWhiskersToggled] = React.useState<boolean>();

  /**
   * @description subscribe to trend report settings
   */
  React.useEffect((): (() => void) => {
    const subscriber = trendReportSettingsProvider().subscribe(
      (mSettings: ITrendReportSettingsState): void => {
        setTemplates(mSettings?.templates);
        setSelectedTemplate(mSettings?.selected);
        setInitialSettings(mSettings?.selected);
        setWhiskersToggled(mSettings?.whiskers);
        /// dont override timeframe unless it actually has a value
        if (!isEmpty(mSettings?.timeframe)) {
          setTimeframe(mSettings?.timeframe ?? null);
        }
        setLoading(false);
      }
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description saves the change to database
   */
  const handleSave = async (): Promise<void> => {
    /// make sure there was an actual change
    if (!isEqual(selectedTemplate?.id, initialSettings?.id)) {
      props.onSave();
    }
    setLoading(true);
    await saveTrendSettings(selectedTemplate, timeframe, whiskersToggled);
    props.onClose();
  };

  /**
   * @description handles change to the selected template
   * @param mSelectedTemplate
   */
  const templateChangeHandler = (
    mSelectedTemplate: ISettingsTemplate<ITrendReportSettings>
  ): void => {
    setSelectedTemplate(mSelectedTemplate);
  };

  /**
   * @description deletes the supplied template and finds a suitable replacement for the selected template
   * @param template
   */
  const templateDeleteHandler = async (
    template: ISettingsTemplate<ITrendReportSettings>
  ): Promise<void> => {
    const index = templates.findIndex(
      (tmp: ISettingsTemplate<ITrendReportSettings>): boolean =>
        tmp.id === template.id
    );
    /// get either the template before or after the template being deleted
    const replacement: ISettingsTemplate<ITrendReportSettings> =
      index === 0 ? templates?.[1] : templates?.[index - 1];

    await deleteSelectedTemplate(template, replacement);
  };

  /**
   * @description handles the change to the timeframe
   * @param range
   */
  const timeframeHandler = (range: IDateRange): void => {
    setTimeframe(range);
  };

  const isValid = React.useMemo((): boolean => {
    /// make sure the user has selected a template
    if (isEmpty(selectedTemplate)) return false;
    /// make sure the user has selected a valid timeframe
    if (!isValidDateRange(timeframe)) return false;
    /// form is valid and can be submitted
    return true;
  }, [JSON.stringify(timeframe), JSON.stringify(selectedTemplate)]);

  return (
    <PopupCard onClose={props.onClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>Trend Report Settings</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        {loading && <LinearLoading />}
        <CardBody>
          <Row shouldCollapse style={{ height: '100%' }}>
            <LeftColumn>
              <TemplateSelect
                step={1}
                templates={templates}
                onSelect={templateChangeHandler}
                onDelete={templateDeleteHandler}
                selectedTemplateId={selectedTemplate?.id}
                formComponent={TemplateForm}
                reportType={'Trend Report'}
                reportTypeId={'trendReport'}
              />
            </LeftColumn>
            <RightColumn>
              <TimeframeSelector
                onTimeframeChange={timeframeHandler}
                timeframe={timeframe}
                step={2}
              />
              <Toggle
                name='whisker-toggle'
                label='Toggle Intra-Session Min/Max Value Whiskers'
                onChange={(): void =>
                  whiskersToggled
                    ? setWhiskersToggled(false)
                    : setWhiskersToggled(true)
                }
                checked={whiskersToggled}
                value='toggle'
              />
            </RightColumn>
          </Row>
        </CardBody>

        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <SaveButton onClick={handleSave} disabled={loading || !isValid} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

interface IProps extends RouteComponentProps {
  style?: React.CSSProperties;
  onSave: () => void;
}
export const TrendSettings = withRouter((props: IProps): React.ReactElement => {
  const open = (): void =>
    openPopup(
      <Popup onClose={closePopup(props.history)} onSave={props.onSave} />,
      props.history,
      'trend-report-settings'
    );

  return <SettingsButton onClick={open} />;
});
